import { useState, useEffect, useMemo } from "react";
import { useT } from "./i18n";
import { speak, getSpeakLangCode } from "./tts";
import { todayISO, isolateInterpolation } from "./utils";
import { getGrammarLabTranslationCache, setGrammarLabTranslationCache } from "./firebase";
import { translateGrammarPoint } from "./api";
import { ReportMistakeFlow } from "./LessonView";
import {
  getGrammarPoints, getGrammarCategories, getGrammarCefrLevels,
  getGrammarPoint, groupForms, isGrammarPointViewed, reviewGrammarPoint, CADENCE_DAYS,
  buildQuestions, computeContentFingerprint, countGrammarNotReviewed,
} from "./grammarLab";
import grammarBlogLinks from "./grammarBlogLinks.json";

// grammar_point_id -> blog post URL for the "Detailed explanation" link in GrammarPointDetail.
// Only English/Farsi blog reading-language versions exist today (scripts/build-grammar-blog-
// links.js only maps points that have a published post at all); any other base language falls
// back to the English blog post per the agreed behavior, rather than showing no link. Returns
// null (button hidden entirely) when this grammar point has no blog post yet.
function getGrammarBlogUrl(grammarPointId, baseLanguage) {
  const slug = grammarBlogLinks[grammarPointId];
  if (!slug) return null;
  const readingLangCode = baseLanguage === "Farsi" ? "fa" : "en";
  return `https://speafy.com/blog/${readingLangCode}/turkish/${slug}/`;
}

// t() returns the dotted path itself when a key is missing from both the
// current language and the English fallback — used here to detect "no
// translation yet" and fall back to the frozen English content JSON.
function trCategory(t, category) {
  const key = `grammarLab.categories.${category}`;
  const val = t(key);
  return val === key ? category.replace(/_/g, " ") : val;
}
export function trPoint(t, gp, field, jsonField) {
  const key = `grammarLab.points.${gp.grammar_point_id}.${field}`;
  const val = t(key);
  return val === key ? gp[jsonField] : val;
}

// Browse cards are tappable buttons (opening the point's own detail view), so a rule_summary
// containing "(see GP####)" citations can't render those as nested clickable links the way the
// detail view's own rule box does — a button-in-a-button either fights the card's own tap target
// or would need to navigate to a DIFFERENT point mid-list, both bad. Cards get the plain-text
// rule_summary with citations stripped instead; the citation is still one tap away, correctly
// linked, once the learner opens this card's own detail view.
function stripCitations(text) {
  if (!text) return text;
  return text.replace(/\s*\(see GP\d{4}\)/g, "").replace(/\s{2,}/g, " ").trim();
}

// Detail-view-only field resolver with three trust tiers, highest first: (1) the curated
// static i18n bundle (title/ruleSummary only — the same mechanism as trPoint above, kept as
// top priority since it predates and outranks this pipeline); (2) the dynamic per-language
// machine translation from the grammarLabTranslations pipeline; (3) the English source.
// `explanation` has no tier-1 entry in any bundle, so t(key) always falls through there,
// landing it on tier 2/3 correctly with no special-casing needed. Returns whether tier 2 is
// what's actually being shown, so the caller only displays the "unverified machine
// translation" badge when it's literally true for THIS field (never for tier 1 or 3 text).
function trFieldWithTier(t, gp, i18nField, jsonField, translation, translationField) {
  const key = `grammarLab.points.${gp.grammar_point_id}.${i18nField}`;
  const val = t(key);
  if (val !== key) return { text: val, isMachineTranslated: false };
  const dyn = translation?.[translationField];
  if (dyn) return { text: dyn, isMachineTranslated: translation.verification_status === "unverified_machine_translation" };
  return { text: gp[jsonField], isMachineTranslated: false };
}

// Overlays a dynamic translation's per-entry meaning (and, for usage_list items, each
// example's translation; for paradigm forms, the shared base_meaning group header) onto the
// English source forms[]/items[], matched by form_id/item_id — never by array position, since
// the schema keeps those IDs stable specifically so a translation can cross-reference the
// English source correctly even if the model reorders anything. Falls back to the English
// entry untouched when no matching translated entry exists (e.g. mid-generation, or a
// translation that predates a content update adding new forms).
function mergeTranslatedEntries(entries, idField, translatedEntries) {
  if (!translatedEntries?.length) return entries;
  const byId = new Map(translatedEntries.map((e) => [e[idField], e]));
  return (entries || []).map((entry) => {
    const tr = byId.get(entry[idField]);
    if (!tr) return entry;
    const merged = { ...entry, meaning: tr.meaning || entry.meaning };
    if (entry.examples && tr.examples) {
      merged.examples = entry.examples.map((ex, i) => ({ ...ex, translation: tr.examples[i]?.translation || ex.translation }));
    }
    if (tr.baseMeaning) merged.base_meaning = tr.baseMeaning;
    return merged;
  });
}

// point_type "structural"'s equivalent of mergeTranslatedEntries above — matched by example_id
// instead of form_id/item_id, and merges `translation`/`structural_note` directly (the flat
// shape structural entries use: {example_id, translation, structural_note}) rather than a
// nested `meaning`/`examples` shape. Falls back to the English entry untouched when no matching
// translated entry exists, same as mergeTranslatedEntries.
function mergeTranslatedExamples(examples, translatedEntries) {
  if (!translatedEntries?.length) return examples;
  const byId = new Map(translatedEntries.map((e) => [e.example_id, e]));
  return (examples || []).map((ex) => {
    const tr = byId.get(ex.example_id);
    if (!tr) return ex;
    return { ...ex, translation: tr.translation || ex.translation, structural_note: tr.structural_note || ex.structural_note };
  });
}

// Small "unverified machine translation" flag — per spec, only ever placed next to
// explanation/ruleSummary, never on title or form/item meanings (those are treated at the
// same confidence as the existing Layer 2 vocabulary translations, which show no indicator).
function MachineTranslatedBadge() {
  return (
    <span
      title="Machine-translated, not yet verified"
      aria-label="Machine-translated, not yet verified"
      style={{ marginLeft: 6, fontSize: "0.8125rem", opacity: 0.65, cursor: "help", verticalAlign: "middle" }}
    >
      🌐
    </span>
  );
}

// Matches the literal "(see GP####)" citation convention used in explanation
// text (see grammar-lab-content.json meta.cross_reference_convention). This
// is the ONLY mention-detection logic in Grammar Lab, by design — no fuzzy
// or semantic matching, so there are no false positives/negatives to worry
// about.
const CITATION_RE = /\(see (GP\d{4})\)/g;

// Same three-tier resolution as trFieldWithTier above, but for a citation
// TARGET's title rather than the currently-viewed point's own fields: (1)
// curated i18n bundle, (2) citationTitles — pre-fetched grammarLabTranslations
// cache entries for cited points, keyed by grammar_point_id (see the
// citationTitles effect in GrammarPointDetail), (3) English source.
function resolveCitationTitle(t, target, citationTitles) {
  const key = `grammarLab.points.${target.grammar_point_id}.title`;
  const val = t(key);
  if (val !== key) return val;
  return citationTitles[target.grammar_point_id] || target.title;
}

// Scans every field that ever passes through renderExplanation (see the citationTitles effect
// in GrammarPointDetail) for "(see GP####)" markers — not just the top-level explanation, since
// forms/items/examples/rule_summary can carry their own citations too (e.g. GP0024's F0146 cites
// GP0017, GP0057's I0084 cites GP0001, GP0027's EX0002 cites GP0006, GP0068's rule_summary cites
// GP0029/GP0007/GP0024/GP0036/GP0043). Reads the English gp fields (never the translation) since
// citation markers are guaranteed preserved verbatim across languages — this lets the fetch start
// immediately, without waiting on the point's own translation to load.
function collectCitationTargets(gp) {
  const texts = [gp.explanation, gp.rule_summary];
  (gp.forms || []).forEach((f) => texts.push(f.meaning));
  (gp.items || []).forEach((it) => {
    texts.push(it.meaning);
    (it.examples || []).forEach((ex) => texts.push(ex.note));
  });
  (gp.examples || []).forEach((ex) => texts.push(ex.structural_note));
  const ids = new Set();
  for (const text of texts) {
    if (typeof text !== "string") continue;
    for (const m of text.matchAll(CITATION_RE)) ids.add(m[1]);
  }
  return [...ids];
}

// Splits an explanation string on every (see GP####) citation and replaces
// each with a clickable link to that point's detail view. The link text is
// the target's title looked up live from the loaded data, never hardcoded,
// so it stays correct if a title is edited. A citation whose target isn't in
// the loaded (filtered) data renders as inert plain text plus a console
// warning — it must never crash or silently vanish.
function renderExplanation(langCode, explanation, onNavigate, t, citationTitles) {
  const nodes = [];
  let lastIndex = 0;
  let match;
  let key = 0;
  CITATION_RE.lastIndex = 0;
  while ((match = CITATION_RE.exec(explanation)) !== null) {
    const [full, targetId] = match;
    if (match.index > lastIndex) nodes.push(explanation.slice(lastIndex, match.index));
    const target = getGrammarPoint(langCode, targetId);
    if (target) {
      nodes.push(
        <button key={key++} type="button" className="gl-xref-link" onClick={() => onNavigate(targetId)}>
          {isolateInterpolation(t("grammarLab.seeReference"), "term", resolveCitationTitle(t, target, citationTitles))}
        </button>
      );
    } else {
      console.warn(`Grammar Lab: explanation cites unknown grammar_point_id "${targetId}" — rendering as plain text.`);
      nodes.push(full);
    }
    lastIndex = match.index + full.length;
  }
  if (lastIndex < explanation.length) nodes.push(explanation.slice(lastIndex));
  return nodes;
}

// ═══════════════════════════════════════════════════════════════
// GRAMMAR LAB — top-level shell. Internal view (list/detail/practice)
// is local state; the tab always re-opens on the browse list.
// ═══════════════════════════════════════════════════════════════
export function GrammarLabView({ profile, grammarProgress, onReview, onMarkViewed, onBack, initialPointId, onInitialPointConsumed }) {
  // initialPointId is a one-shot deep-link (e.g. from global search): only consulted in these
  // initializers, which run once at mount. Normal bottom-nav visits pass it as null/undefined,
  // so the tab still always re-opens on the browse list as documented above.
  const [view, setView] = useState(initialPointId ? "detail" : "list");
  const [selectedId, setSelectedId] = useState(initialPointId || null);
  const baseLanguage = profile?.baseLanguage || "English";
  const langCode = (profile?.targetLanguage || "Turkish").toLowerCase();

  useEffect(() => {
    if (initialPointId) {
      onMarkViewed?.(initialPointId);
      onInitialPointConsumed?.();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openPoint = (id) => { setSelectedId(id); setView("detail"); onMarkViewed?.(id); };
  const backToList = () => { setView("list"); setSelectedId(null); };

  if (view === "detail" && selectedId) {
    return (
      <GrammarPointDetail
        grammarPointId={selectedId}
        langCode={langCode}
        baseLanguage={baseLanguage}
        onBack={backToList}
        onPractice={() => setView("practice")}
        onNavigateToPoint={openPoint}
      />
    );
  }
  if (view === "practice" && selectedId) {
    return (
      <GrammarPractice
        grammarPointId={selectedId}
        langCode={langCode}
        grammarProgress={grammarProgress}
        onReview={onReview}
        onBack={() => setView("detail")}
      />
    );
  }
  return <GrammarBrowseList langCode={langCode} grammarProgress={grammarProgress} onOpen={openPoint} onBack={onBack} />;
}

// ═══════════════════════════════════════════════════════════════
// BROWSE LIST
// ═══════════════════════════════════════════════════════════════
function GrammarBrowseList({ langCode, grammarProgress, onOpen, onBack }) {
  const { t } = useT();
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [cefr, setCefr] = useState("");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return getGrammarPoints(langCode).filter((gp) => {
      if (category && gp.category !== category) return false;
      if (cefr && gp.cefr_level !== cefr) return false;
      if (q) {
        const title = trPoint(t, gp, "title", "title").toLowerCase();
        const rule = trPoint(t, gp, "ruleSummary", "rule_summary").toLowerCase();
        if (!title.includes(q) && !rule.includes(q)) return false;
      }
      return true;
    });
  }, [langCode, search, category, cefr, t]);

  // Not-reviewed count (total minus viewed) — matches the bottom-nav Grammar
  // Lab badge, not the spaced-repetition due count (which stays near the
  // full total since never-reviewed points are always "due").
  const dueCount = countGrammarNotReviewed(langCode, grammarProgress);

  return (
    <div className="gl-view">
      <div className="lesson-header">
        <button className="back-btn" onClick={onBack}>{t("grammarLab.backBtn")}</button>
        <div>
          <div className="lesson-title">{t("grammarLab.title")}</div>
          <div style={{ fontSize: "0.8125rem", color: "#8B7355" }}>{t("grammarLab.subtitle", { count: getGrammarPoints(langCode).length })}</div>
        </div>
        {dueCount > 0 && <span className="gl-due-pill">📘 {t("grammarLab.dueBadge", { n: dueCount })}</span>}
      </div>

      <div className="al-search-wrap">
        <input
          className="text-input al-search"
          placeholder={t("grammarLab.searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && <button className="al-clear-btn" onClick={() => setSearch("")}>✕</button>}
      </div>

      <div className="gl-filter-row">
        <select className="gl-filter-select" value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">{t("grammarLab.allCategories")}</option>
          {getGrammarCategories(langCode).map((c) => <option key={c} value={c}>{trCategory(t, c)}</option>)}
        </select>
        <select className="gl-filter-select" value={cefr} onChange={(e) => setCefr(e.target.value)}>
          <option value="">{t("grammarLab.allLevels")}</option>
          {getGrammarCefrLevels(langCode).map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
      </div>

      {filtered.length === 0 ? (
        <div className="empty-state">{isolateInterpolation(t("grammarLab.noResults"), "search", search)}</div>
      ) : (
        <div className="gl-grid">
          {filtered.map((gp) => {
            const viewed = isGrammarPointViewed(grammarProgress?.[gp.grammar_point_id]);
            return (
              <button key={gp.grammar_point_id} className="gl-card" onClick={() => onOpen(gp.grammar_point_id)}>
                <div className="gl-card-title">{trPoint(t, gp, "title", "title")}</div>
                <div className="gl-card-summary">{stripCitations(trPoint(t, gp, "ruleSummary", "rule_summary"))}</div>
                <div className="gl-card-meta">
                  <span className="gl-badge gl-badge-cefr">{gp.cefr_level}</span>
                  <span className="gl-badge gl-badge-category">{trCategory(t, gp.category)}</span>
                  <span className={`gl-badge ${viewed ? "gl-badge-reviewed" : "gl-badge-notreviewed"}`}>
                    {viewed ? t("grammarLab.reviewedLabel") : t("grammarLab.notReviewedLabel")}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// POINT DETAIL
// ═══════════════════════════════════════════════════════════════
function GrammarPointDetail({ grammarPointId, langCode, baseLanguage = "English", onBack, onPractice, onNavigateToPoint }) {
  const { t } = useT();
  const gp = getGrammarPoint(langCode, grammarPointId);

  // Dynamic per-language machine translation (grammarLabTranslations pipeline). All hooks run
  // unconditionally, before the `!gp` early return below, so hook order never depends on
  // whether grammarPointId happens to resolve — see the `if (!gp) return;` guard inside the
  // effect itself, not around the useState/useEffect calls.
  const [translation, setTranslation] = useState(null);

  useEffect(() => {
    setTranslation(null);
    if (!gp || baseLanguage === "English") return;
    const cacheKey = `${langCode}_${gp.grammar_point_id}_${baseLanguage}`;
    const currentFingerprint = computeContentFingerprint(gp);
    getGrammarLabTranslationCache(cacheKey)
      .then((cached) => {
        // A cached translation with no fingerprint (written before this check existed) or one
        // that no longer matches the point's current English content is stale — the source
        // changed since this translation was generated. Treat exactly like a cache miss: fall
        // through and regenerate, overwriting the stale entry with a freshly-stamped one below.
        if (cached && cached.contentFingerprint === currentFingerprint) { setTranslation(cached); return; }
        if (cached) console.warn(`Grammar Lab: cached translation for ${cacheKey} is stale (source changed since it was generated) — regenerating`);
        return translateGrammarPoint(gp, baseLanguage).then((result) => {
          setGrammarLabTranslationCache(cacheKey, result).catch((err) =>
            console.error(`Grammar Lab: failed to cache translation for ${cacheKey}`, err));
          setTranslation(result);
        });
      })
      // Cache lookup or generation failed — fall back to English rather than blocking the
      // page, logged via console.error. LessonView.js's own Layer 2 fallback now logs this
      // same failure mode too (added for Track A2), distinct from the intentional
      // frozen-language console.warn inside translateLessonWords (api.js) — these are two
      // different failure modes and both stay distinguishable in the console.
      .catch((err) => console.error(`Grammar Lab: translation unavailable for ${cacheKey}, falling back to English`, err));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grammarPointId, baseLanguage]);

  // Citation link titles (see resolveCitationTitle above) — pre-fetches the grammarLabTranslations
  // cache for every point cited via "(see GP####)" in this point's explanation, so a citation
  // link can show the target's own title in baseLanguage instead of always falling back to
  // English. Cache-only, never calls translateGrammarPoint: a citation is a small link label,
  // not the point the user is actually viewing, so a missing cache entry just falls back to
  // English rather than triggering a fresh translation generation for it.
  const [citationTitles, setCitationTitles] = useState({});

  useEffect(() => {
    setCitationTitles({});
    if (!gp || baseLanguage === "English") return;
    const targetIds = collectCitationTargets(gp);
    if (!targetIds.length) return;
    Promise.all(
      targetIds.map((id) =>
        getGrammarLabTranslationCache(`${langCode}_${id}_${baseLanguage}`)
          .then((cached) => {
            // Same staleness check as the main translation effect above, applied to each cited
            // target: a citation label built from a stale cached title would show old wording
            // for a point the learner hasn't even opened yet. A mismatch/missing fingerprint
            // falls back to null here, same as a cache miss — resolveCitationTitle's own tier-3
            // English fallback already handles that correctly, no separate regeneration needed
            // (per this effect's cache-only design, unchanged).
            const target = getGrammarPoint(langCode, id);
            const fresh = target && cached?.contentFingerprint === computeContentFingerprint(target);
            return [id, fresh ? cached.title : null];
          })
          .catch(() => [id, null])
      )
    ).then((pairs) => setCitationTitles(Object.fromEntries(pairs.filter(([, title]) => title))));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grammarPointId, baseLanguage]);

  if (!gp) return null;

  const titleField = trFieldWithTier(t, gp, "title", "title", translation, "title");
  const ruleSummaryField = trFieldWithTier(t, gp, "ruleSummary", "rule_summary", translation, "ruleSummary");
  const explanationField = trFieldWithTier(t, gp, "explanation", "explanation", translation, "explanation");

  const translatedForms = gp.point_type === "paradigm" || !gp.point_type
    ? mergeTranslatedEntries(gp.forms, "form_id", translation?.forms_or_items)
    : gp.forms;
  const translatedItems = gp.point_type === "usage_list"
    ? mergeTranslatedEntries(gp.items, "item_id", translation?.forms_or_items)
    : gp.items;
  const translatedExamples = gp.point_type === "structural"
    ? mergeTranslatedExamples(gp.examples, translation?.forms_or_items)
    : gp.examples;

  return (
    <div className="gl-view">
      <div className="lesson-header">
        <button className="back-btn" onClick={onBack}>{t("grammarLab.backBtn")}</button>
        <div>
          <div className="lesson-title">{titleField.text}</div>
          <div className="gl-card-meta" style={{ marginTop: 4 }}>
            <span className="gl-badge gl-badge-cefr">{gp.cefr_level}</span>
            <span className="gl-badge gl-badge-category">{trCategory(t, gp.category)}</span>
          </div>
        </div>
      </div>

      {baseLanguage !== "English" && !translation && (
        <p style={{ fontSize: "0.75rem", color: "#8B7355", margin: "0 0 12px", fontStyle: "italic" }}>
          {t("grammarLab.translatingNote")}
        </p>
      )}

      <div className="gl-rule">
        {renderExplanation(langCode, ruleSummaryField.text, onNavigateToPoint, t, citationTitles)}
        {ruleSummaryField.isMachineTranslated && <MachineTranslatedBadge />}
      </div>

      <div className="gl-explanation">
        <div className="gl-explanation-label">
          {t("grammarLab.explanationLabel")}
          {explanationField.isMachineTranslated && <MachineTranslatedBadge />}
        </div>
        {renderExplanation(langCode, explanationField.text, onNavigateToPoint, t, citationTitles)}
      </div>

      {gp.point_type === "usage_list" ? (
        <GrammarItemsList langCode={langCode} items={translatedItems} onNavigate={onNavigateToPoint} citationTitles={citationTitles} />
      ) : gp.point_type === "structural" ? (
        <GrammarExamplesList langCode={langCode} examples={translatedExamples} onNavigate={onNavigateToPoint} citationTitles={citationTitles} />
      ) : (
        <GrammarFormsList langCode={langCode} forms={translatedForms} onNavigate={onNavigateToPoint} citationTitles={citationTitles} />
      )}

      {translation && (
        <ReportMistakeFlow
          buildCheckPrompt={() => `You are a Turkish grammar explanation translation quality evaluator. Evaluate the accuracy of this translated grammar point.

Original English title: "${gp.title}"
Translated title (${baseLanguage}): "${translation.title}"
Original English rule summary: "${gp.rule_summary}"
Translated rule summary (${baseLanguage}): "${translation.ruleSummary}"
Original English explanation: "${gp.explanation}"
Translated explanation (${baseLanguage}): "${translation.explanation}"

Assess whether the translation is accurate and natural, and confirm any "(see GP####)" citation markers in the explanation were preserved unchanged, character-for-character. If you find any issue, describe it briefly in one sentence, written in ${baseLanguage}. Respond ONLY with valid JSON, no markdown: {"confidence":0-100,"suspicious":true/false,"reason":"one sentence, in ${baseLanguage}"}`}
          buildReportPayload={(origin) => ({
            grammarPointId: gp.grammar_point_id,
            lang: baseLanguage,
            reportType: origin,
            contentSource: "grammar-lab-translation",
            langCode,
          })}
        />
      )}

      {(() => {
        const blogUrl = getGrammarBlogUrl(gp.grammar_point_id, baseLanguage);
        return blogUrl ? (
          <a className="gold-btn" href={blogUrl} target="_blank" rel="noopener noreferrer" style={{ marginBottom: 12 }}>
            {t("grammarLab.detailedExplanationBtn")}
          </a>
        ) : null;
      })()}

      {!gp.no_practice_drill && (
        <button className="primary-btn" onClick={onPractice}>{t("grammarLab.practiceBtn")}</button>
      )}
    </div>
  );
}

// point_type: "paradigm" (and the original 18 points, which predate the
// point_type field entirely) — forms[] grouped by their base word. `meaning`
// runs through renderExplanation() too — the SAME citation-link function used
// for the top-level explanation, not a second implementation — since a form's
// meaning can carry its own "(see GP####)" citation (e.g. GP0024's F0146:
// "I apparently went (note: no t→d change - see GP0017 contrast)").
function GrammarFormsList({ langCode, forms, onNavigate, citationTitles }) {
  const { t } = useT();
  const groups = groupForms(forms);
  return (
    <>
      <div className="section-title">{t("grammarLab.formsLabel")}</div>
      {groups.map((g) => (
        <div className="gl-group" key={g.base}>
          <div className="gl-group-title" dir="auto">{g.base}</div>
          <div className="gl-group-meaning">{g.baseMeaning}</div>
          {g.forms.map((f) => (
            <div className="gl-form-row" key={f.form_id}>
              <div style={{ minWidth: 0 }}>
                <div className="gl-form-surface" dir="auto">{f.surface}</div>
                <div className="gl-form-meaning">{renderExplanation(langCode, f.meaning, onNavigate, t, citationTitles)}</div>
                <div className="gl-form-breakdown"><BreakdownChips breakdown={f.breakdown} /></div>
              </div>
              <button className="tts-btn" onClick={() => speak(f.surface, null, getSpeakLangCode(langCode))}>🔊</button>
            </div>
          ))}
        </div>
      ))}
    </>
  );
}

// point_type: "usage_list" — a closed list of separate words/constructions
// (items[]) instead of suffix-derived forms. Each item can carry 1-3 examples
// (e.g. GP0019's vowels, GP0020's harmony pairs) — render all of them, not
// just the first. Both `meaning` and each example's `note` run through
// renderExplanation() for the same reason as GrammarFormsList's `meaning`
// above (e.g. GP0039's items all cite the tense points they compare, GP0057's
// I0084 cites GP0001 from inside its note).
function GrammarItemsList({ langCode, items, onNavigate, citationTitles }) {
  const { t } = useT();
  return (
    <>
      <div className="section-title">{t("grammarLab.formsLabel")}</div>
      {(items || []).map((it) => (
        <div className="gl-group" key={it.item_id}>
          <div className="gl-group-title" dir="auto">{it.word}</div>
          <div className="gl-group-meaning">{renderExplanation(langCode, it.meaning, onNavigate, t, citationTitles)}</div>
          {(it.examples || []).map((ex, i) => (
            <div className="gl-form-row" key={i}>
              <div style={{ minWidth: 0 }}>
                <div className="gl-form-surface" dir="auto">{ex.turkish}</div>
                <div className="gl-form-meaning">{ex.translation}</div>
                {ex.note && <div className="gl-form-breakdown">{renderExplanation(langCode, ex.note, onNavigate, t, citationTitles)}</div>}
              </div>
              <button className="tts-btn" onClick={() => speak(ex.turkish, null, getSpeakLangCode(langCode))}>🔊</button>
            </div>
          ))}
        </div>
      ))}
    </>
  );
}

// point_type: "structural" — a flat examples[] list illustrating a syntactic
// principle (e.g. GP0027 word order); there is no separate "form" being
// taught, so each example's structural_note IS the teaching content. These
// points always carry no_practice_drill: true (enforced by the caller, which
// omits the Practice button entirely rather than showing an empty drill).
// structural_note is the structural-point equivalent of usage_list's `note`
// field (explanatory prose about an example) — not in the task's originally
// listed fields, but included for the same citation-link treatment since it's
// the same kind of field under a different key, and GP0027's own EX0002
// already has a live, currently-unlinked "(see GP0006)" in exactly this field.
function GrammarExamplesList({ langCode, examples, onNavigate, citationTitles }) {
  const { t } = useT();
  return (
    <>
      <div className="section-title">{t("grammarLab.formsLabel")}</div>
      {(examples || []).map((ex) => (
        <div className="gl-form-row" key={ex.example_id}>
          <div style={{ minWidth: 0 }}>
            <div className="gl-form-surface" dir="auto">{ex.turkish}</div>
            <div className="gl-form-meaning">{ex.translation}</div>
            <div className="gl-form-breakdown">{renderExplanation(langCode, ex.structural_note, onNavigate, t, citationTitles)}</div>
          </div>
          <button className="tts-btn" onClick={() => speak(ex.turkish, null, getSpeakLangCode(langCode))}>🔊</button>
        </div>
      ))}
    </>
  );
}

function BreakdownChips({ breakdown }) {
  if (!breakdown) return null;
  const parts = breakdown.split(" + ").map((s) => s.trim()).filter(Boolean);
  if (parts.length < 2) {
    return <span style={{ fontSize: "0.75rem", color: "#8B7355", fontStyle: "italic" }} dir="auto">{breakdown}</span>;
  }
  return (
    <div className="morph-row" dir="ltr">
      {parts.map((p, i) => (
        <span key={i} style={{ display: "flex", alignItems: "center", gap: 3 }}>
          {i > 0 && <span className="morph-sep">+</span>}
          <span className={`morph-chip morph-${i === 0 ? "root" : "suffix"}`} dir="auto">{p}</span>
        </span>
      ))}
    </div>
  );
}

function GrammarPractice({ grammarPointId, langCode, grammarProgress, onReview, onBack }) {
  const { t } = useT();
  const gp = getGrammarPoint(langCode, grammarPointId);
  const [questions] = useState(() => (gp ? buildQuestions(gp, 5, langCode) : []));
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState(null);
  const [correctCount, setCorrectCount] = useState(0);
  const [done, setDone] = useState(false);
  const [nextDueDays, setNextDueDays] = useState(null);

  if (!gp || questions.length === 0) return null;

  const q = questions[idx];
  const isLast = idx === questions.length - 1;

  const pick = (opt) => {
    if (picked) return;
    setPicked(opt);
    if (opt === q.answer) setCorrectCount((c) => c + 1);
  };

  const next = () => {
    if (!isLast) {
      setIdx((i) => i + 1);
      setPicked(null);
      return;
    }
    const passed = correctCount / questions.length >= 0.7;
    const entry = reviewGrammarPoint(grammarProgress?.[grammarPointId], passed, todayISO());
    onReview(grammarPointId, entry);
    setNextDueDays(CADENCE_DAYS[entry.cadenceStep]);
    setDone(true);
  };

  if (done) {
    const passed = correctCount / questions.length >= 0.7;
    return (
      <div className="gl-view">
        <div className="gl-practice-card">
          <div className="gl-practice-summary">{t("grammarLab.practiceDone")}</div>
          <div style={{ fontSize: "1rem", color: "#5C3D2E", marginBottom: 12 }}>
            {t("grammarLab.practiceScore", { correct: correctCount, total: questions.length })}
          </div>
          <div style={{ fontSize: "0.875rem", color: "#8B7355", marginBottom: 20 }}>
            {passed
              ? t("grammarLab.practiceNextReview", { days: nextDueDays, plural: nextDueDays === 1 ? "" : "s" })
              : t("grammarLab.practiceReset")}
          </div>
          <button className="primary-btn" onClick={onBack}>{t("grammarLab.practiceCloseBtn")}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="gl-view">
      <div className="lesson-header">
        <button className="back-btn" onClick={onBack}>{t("grammarLab.backBtn")}</button>
        <div className="lesson-title">{t("grammarLab.practiceTitle")} · {trPoint(t, gp, "title", "title")}</div>
      </div>
      <div className="gl-practice-card">
        <div className="gl-practice-progress">{t("grammarLab.practiceOf", { current: idx + 1, total: questions.length })}</div>
        <div className="gl-practice-prompt">
          {t("grammarLab.practiceQuestion")}<br />
          <em><bdi>"{q.prompt}"</bdi></em>
        </div>
        <div className="gl-practice-options">
          {q.options.map((opt) => {
            let cls = "gl-practice-option";
            if (picked) {
              if (opt === q.answer) cls += " gl-practice-option--correct";
              else if (opt === picked) cls += " gl-practice-option--incorrect";
              else cls += " gl-practice-option--disabled";
            }
            return <button key={opt} className={cls} onClick={() => pick(opt)} dir="auto">{opt}</button>;
          })}
        </div>
        {picked && (
          <>
            <div className={`gl-practice-feedback ${picked === q.answer ? "gl-practice-feedback--correct" : "gl-practice-feedback--incorrect"}`}>
              {picked === q.answer ? t("grammarLab.practiceCorrectFeedback") : isolateInterpolation(t("grammarLab.practiceIncorrectFeedback"), "answer", q.answer)}
            </div>
            <button className="primary-btn" style={{ marginTop: 14 }} onClick={next}>
              {isLast ? t("grammarLab.practiceDone") : t("grammarLab.practiceNext")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
